import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "./api.ts";

export interface ApiState<T> {
  data: T | undefined;
  error: ApiError | undefined;
  loading: boolean;
  reload: () => void;
}

interface Slot<T> {
  key: string;
  data?: T;
  error?: ApiError;
}

/** GET `path` (null = skip); refetches when the path, the UI language or reload() changes. */
export function useApi<T>(path: string | null): ApiState<T> {
  const { i18n } = useTranslation();
  const [nonce, setNonce] = useState(0);
  const key = path === null ? "" : `${path}|${i18n.language}|${nonce}`;
  const [slot, setSlot] = useState<Slot<T>>({ key: "" });

  useEffect(() => {
    if (path === null) return;
    let live = true;
    api<T>(path).then(
      (data) => live && setSlot({ key, data }),
      (e: unknown) =>
        live && setSlot({ key, error: e instanceof ApiError ? e : new ApiError(0, { code: "network_error", message: String(e) }) }),
    );
    return () => {
      live = false;
    };
  }, [path, key]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const fresh = slot.key === key;
  // Keep showing the previous data while a reload is in flight (no flicker).
  return { data: slot.data, error: fresh ? slot.error : undefined, loading: path !== null && !fresh, reload };
}
